import { useMemo } from 'react';
import dayjs from 'dayjs';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useAuthPage } from '@features/auth/useAuthPage';
import {
  createBackofficeOAuthClient,
  createBackofficeTenant,
  createBackofficeUser,
  deleteBackofficeTenant,
  deleteBackofficeUser,
  exportBackofficeSeries,
  getBackofficeRoles,
  getBackofficeSeries,
  getBackofficeTenants,
  getBackofficeUsers,
  updateBackofficeTenant,
  updateBackofficeUser,
} from './api';
import type {
  CreateTenantBody,
  CreateUserBody,
  SeriesFilters,
  SeriesItem,
  SeriesRow,
  Tenant,
  UpdateTenantBody,
  UpdateUserBody,
  User,
  UserFilters,
} from './types';

export function useBackofficeSession() {
  const { accessToken, user } = useAuthPage();

  return useMemo(() => {
    const roleName = user?.role?.name?.toLowerCase();
    const isAdmin = Boolean(user?.role?.isAdmin || roleName === 'admin');

    return {
      accessToken,
      user,
      isAdmin,
      isAuthenticated: Boolean(accessToken),
    };
  }, [accessToken, user]);
}

function formatValue(value: unknown) {
  if (value === undefined || value === null || value === '') return '-';
  return String(value);
}

function formatDate(value: string | null | undefined) {
  if (!value) return '-';
  return dayjs(value).isValid() ? dayjs(value).format('MMM DD, YYYY') : value;
}

function formatImageCount(series: SeriesItem) {
  const count =
    series.seriesImages ??
    series.imageTag?.number_of_frames ??
    series.imageTag?.depth;

  if (count === undefined || count === null || count === '') return '-';

  const numericValue = Number(count);
  return Number.isFinite(numericValue) ? String(count) : '-';
}

function normalizeSearchValue(value: unknown) {
  if (value === undefined || value === null) return '';
  return String(value).trim().toLowerCase();
}

function matchesText(value: unknown, filterValue?: string) {
  const normalizedFilter = normalizeSearchValue(filterValue);
  if (!normalizedFilter) return true;

  return normalizeSearchValue(value).includes(normalizedFilter);
}

function normalizeDateKey(value: string | null | undefined) {
  if (!value) return undefined;

  const rawValue = String(value).trim();
  const digits = rawValue.replace(/\D/g, '');

  if (digits.length >= 8) return digits.slice(0, 8);

  const parsedDate = dayjs(rawValue);
  return parsedDate.isValid() ? parsedDate.format('YYYYMMDD') : undefined;
}

function matchesDateRange(
  value: string | null | undefined,
  startDate?: string,
  endDate?: string,
) {
  if (!startDate && !endDate) return true;

  const dateKey = normalizeDateKey(value);
  if (!dateKey) return false;

  if (startDate && dateKey < startDate) return false;
  if (endDate && dateKey > endDate) return false;

  return true;
}

function getCreatedAtRange(createdAt?: string) {
  if (!createdAt) {
    return { startDate: undefined, endDate: undefined };
  }

  const [startDate, endDate] = createdAt.split('-', 2);
  return {
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  };
}

function filterSeriesItems(seriesItems: SeriesItem[], filters: SeriesFilters) {
  const createdAtRange = getCreatedAtRange(filters.createdAt);

  return seriesItems.filter((series) => {
    if (filters.tenantId && series.tenantId !== filters.tenantId) return false;

    if (
      !matchesText(
        series.manufacturer ?? series.imageTag?.manufacturer,
        filters.manufacturer,
      )
    ) {
      return false;
    }

    if (
      !matchesText(
        series.manufacturerModel ?? series.imageTag?.manufacturer_model_name,
        filters.manufacturerModel,
      )
    ) {
      return false;
    }

    if (!matchesDateRange(series.seriesDate, filters.startDate, filters.endDate)) {
      return false;
    }

    if (
      !matchesDateRange(
        series.createdAt,
        createdAtRange.startDate,
        createdAtRange.endDate,
      )
    ) {
      return false;
    }

    return true;
  });
}

function getUserTenantId(user: User) {
  return user.tenant?.id ?? user.tenantId;
}

function getUserFilterName(user: User) {
  return user.user_name ?? user.name;
}

function filterUsers(users: User[], filters: UserFilters) {
  return users.filter((user) => {
    if (filters.tenantId && getUserTenantId(user) !== filters.tenantId) {
      return false;
    }

    return matchesText(getUserFilterName(user), filters.user_name);
  });
}

export function toSeriesRow(
  series: SeriesItem,
  tenantNameById: Record<string, string> = {},
): SeriesRow {
  return {
    id: series.id,
    statusId: series.status?.id,
    tenant: formatValue(
      series.tenantName ??
        (series.tenantId ? tenantNameById[series.tenantId] : undefined) ??
        series.tenantId,
    ),
    manufacturer: formatValue(
      series.manufacturer ?? series.imageTag?.manufacturer,
    ),
    model: formatValue(
      series.manufacturerModel ?? series.imageTag?.manufacturer_model_name,
    ),
    images: formatImageCount(series),
    seriesDate: formatDate(series.seriesDate),
    description: formatValue(
      series.description ?? series.series_description ?? series.protocol_name,
    ),
    createdAt: formatDate(series.createdAt),
    isPreventDelete: series.preventDelete,
    warningMessage: series.warningMessage,
  };
}

export function useBackofficeSeriesQuery() {
  const { accessToken } = useBackofficeSession();

  return useInfiniteQuery({
    queryKey: ['backoffice', 'series', accessToken],
    initialPageParam: 1,
    enabled: Boolean(accessToken),
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: ({ pageParam }) =>
      getBackofficeSeries({
        accessToken,
        params: {
          page: pageParam,
          limit: 20,
          sort: JSON.stringify([]),
        },
      }),
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasNextPage ? allPages.length + 1 : undefined,
  });
}

export function useBackofficeSeriesRows(filters: SeriesFilters) {
  const query = useBackofficeSeriesQuery();
  const tenantsQuery = useBackofficeTenantsQuery();
  const tenantNameById = useMemo(
    () =>
      Object.fromEntries(
        (tenantsQuery.data ?? []).map((tenant) => [tenant.id, tenant.name]),
      ),
    [tenantsQuery.data],
  );
  const rows = useMemo(
    () =>
      filterSeriesItems(
        (query.data?.pages ?? []).flatMap((page) => page.data),
        filters,
      )
        .map((series) => toSeriesRow(series, tenantNameById)),
    [filters, query.data?.pages, tenantNameById],
  );

  return { ...query, rows };
}

export function useBackofficeSeriesExportMutation() {
  const { accessToken } = useBackofficeSession();

  return useMutation({
    mutationFn: async (id: SeriesItem['id']) => {
      if (!accessToken) throw new Error('Access token is required.');
      const response = await exportBackofficeSeries({ accessToken, id });
      const blob = new Blob([response.data], { type: 'application/zip' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');

      anchor.href = downloadUrl;
      anchor.download = `${id}-ExportData.cl`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(downloadUrl);
    },
  });
}

export function useBackofficeTenantsQuery() {
  const { accessToken } = useBackofficeSession();

  return useQuery({
    queryKey: ['backoffice', 'tenants', accessToken],
    enabled: Boolean(accessToken),
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: () => getBackofficeTenants({ accessToken }),
  });
}

export function useCreateTenantMutation() {
  const { accessToken } = useBackofficeSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateTenantBody) => {
      if (!accessToken) throw new Error('Access token is required.');
      return createBackofficeTenant({ accessToken, body });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['backoffice', 'tenants'] }),
  });
}

export function useUpdateTenantMutation() {
  const { accessToken } = useBackofficeSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: Tenant['id']; body: UpdateTenantBody }) => {
      if (!accessToken) throw new Error('Access token is required.');
      return updateBackofficeTenant({ accessToken, id, body });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['backoffice', 'tenants'] }),
  });
}

export function useDeleteTenantMutation() {
  const { accessToken } = useBackofficeSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: Tenant['id']) => {
      if (!accessToken) throw new Error('Access token is required.');
      return deleteBackofficeTenant({ accessToken, id });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['backoffice', 'tenants'] }),
  });
}

export function useCreateOAuthClientMutation() {
  const { accessToken } = useBackofficeSession();

  return useMutation({
    mutationFn: (tenantId: Tenant['id']) => {
      if (!accessToken) throw new Error('Access token is required.');
      return createBackofficeOAuthClient({ accessToken, tenantId });
    },
  });
}

export function useBackofficeUsersQuery() {
  const { accessToken } = useBackofficeSession();

  return useInfiniteQuery({
    queryKey: ['backoffice', 'users', accessToken],
    initialPageParam: 1,
    enabled: Boolean(accessToken),
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: ({ pageParam }) =>
      getBackofficeUsers({
        accessToken,
        params: {
          page: pageParam,
          limit: 20,
        },
      }),
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasNextPage ? allPages.length + 1 : undefined,
  });
}

export function useBackofficeUsers(filters: UserFilters) {
  const query = useBackofficeUsersQuery();
  const users = useMemo(
    () =>
      filterUsers(
        (query.data?.pages ?? []).flatMap((page) => page.data),
        filters,
      ),
    [filters, query.data?.pages],
  );

  return { ...query, users };
}

export function useBackofficeRolesQuery() {
  const { accessToken } = useBackofficeSession();

  return useQuery({
    queryKey: ['backoffice', 'roles', accessToken],
    enabled: Boolean(accessToken),
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: () => getBackofficeRoles({ accessToken }),
  });
}

export function useCreateUserMutation() {
  const { accessToken } = useBackofficeSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateUserBody) => {
      if (!accessToken) throw new Error('Access token is required.');
      return createBackofficeUser({ accessToken, body });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['backoffice', 'users'] }),
  });
}

export function useUpdateUserMutation() {
  const { accessToken } = useBackofficeSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: User['id']; body: UpdateUserBody }) => {
      if (!accessToken) throw new Error('Access token is required.');
      console.log(body)
      return updateBackofficeUser({ accessToken, id, body });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['backoffice', 'users'] }),
  });
}

export function useDeleteUserMutation() {
  const { accessToken } = useBackofficeSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: User['id']) => {
      if (!accessToken) throw new Error('Access token is required.');
      return deleteBackofficeUser({ accessToken, id });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['backoffice', 'users'] }),
  });
}
